import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { buildSuspensionsView, type SuspensionsView } from './suspensionsModel';
import type { Market, SuspensionReasonCode, User } from '../dataProvider/types';

/**
 * The Suspended-users worklist, read through the aggregation seam
 * (`buildSuspensionsView`), NOT computed in the component. For v1 it pulls raw
 * users from the mock provider and aggregates client-side; the real-data migration
 * replaces the fetch-and-aggregate here with a server-side endpoint and the surface
 * does not change. Same seam idiom as useFraudQueue / useSubscriptions.
 *
 * INVESTIGATIVE-GLOBAL: it does NOT read the region switcher. It is global by
 * default; the optional `regionFilter` (the surface's own facet) narrows by the
 * user's market by exact value. `now` is the injected clock the elapsed state is
 * derived against, so the same render is deterministic in tests.
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

export const useSuspensions = (
  now: number,
  regionFilter: Market | null,
  reasonFilter: SuspensionReasonCode | null,
): {
  view: SuspensionsView | null;
  isLoading: boolean;
} => {
  const dataProvider = useDataProvider();

  const { data, isLoading } = useQuery({
    queryKey: ['suspensions', now, regionFilter, reasonFilter],
    queryFn: async () => {
      const users = await dataProvider.getList('users', ALL);
      return buildSuspensionsView(
        { users: users.data as unknown as User[] },
        { now, regionFilter, reasonFilter },
      );
    },
  });

  return { view: data ?? null, isLoading };
};
